import {
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  getSortedRowModel,
  PaginationState,
  getCoreRowModel,
  useReactTable,
  SortingState,
  toast,
} from '@ui/components';
import { RequestFieldFilterType, VehicleTableDataDto, ResponseErrorDto } from '@interfaces/models';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { getPaginatedVehicles, bulkEditVehicles } from '@api/vehicles';
import { useMutation, useQuery } from '@tanstack/react-query';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetCostCenters } from '@shared/hooks';
import { getTableRequest } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useDebounce } from '@ui/hooks';
import { z } from 'zod';

import { useVehicleFilters } from './use-vehicle-filters';
import { getVehiclesColumns } from './vehicles-columns';

export const VEHICLES_TABLE_FILTERS_DEFAULT_VALUES = {
  [RequestFieldFilterType.VEHICLE_REGISTRATION_COUNTRY]: [],
  [RequestFieldFilterType.VEHICLE_CATEGORY]: [],
  [RequestFieldFilterType.VEHICLE_SERVICE]: [],
};

const filterSchema = z.object({
  [RequestFieldFilterType.VEHICLE_REGISTRATION_COUNTRY]: z.array(z.string()),
  [RequestFieldFilterType.VEHICLE_CATEGORY]: z.array(z.string()),
  [RequestFieldFilterType.VEHICLE_SERVICE]: z.array(z.string()),
});

export const useVehiclesTable = ({
  onBulkEditVehiclesSuccess,
  onRemoveVehicle,
  onEditVehicle,
}: {
  onRemoveVehicle: (vehicle: VehicleTableDataDto) => void;
  onEditVehicle: (vehicle: VehicleTableDataDto) => void;
  onBulkEditVehiclesSuccess?: () => void;
}) => {
  const { t } = useTranslation();

  // Use a ref to store the last known totalItems value.
  // This is used to avoid flickering when the data is loading.
  // I do not want to use placeholderData in useQuery because loader is not visible then when changing pagination.
  const totalItemsRef = useRef<number>(0);

  const columns = getVehiclesColumns(t, onEditVehicle, onRemoveVehicle);

  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const debouncedTableSearchQuery = useDebounce(tableSearchQuery, 800);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedCostCenters, setSelectedCostCenters] = useState<string[]>([]);

  // We need to store selected row details to get cost centers. RowSelection keeps only ids.
  const [selectedData, setSelectedData] = useState<VehicleTableDataDto[]>([]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { filtersOptions, isLoading: isFiltersLoading, refetchFilters } = useVehicleFilters();

  const filtersFrom = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues: VEHICLES_TABLE_FILTERS_DEFAULT_VALUES,
  });

  const tableFilters = filtersFrom.watch();

  const request = useMemo(
    () =>
      getTableRequest({
        search: debouncedTableSearchQuery,
        filters: tableFilters,
        pagination,
        sorting,
      }),
    [pagination, sorting, debouncedTableSearchQuery, tableFilters],
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['vehicles-table', request],
    queryFn: () => {
      refetchFilters();
      return getPaginatedVehicles(request);
    },
  });

  const { mutate: bulkEditVehiclesMutation, isPending: isBulkEditVehiclesLoading } = useMutation({
    mutationFn: bulkEditVehicles,
    onSuccess: () => {
      refetch();
      onBulkEditVehiclesSuccess?.();
      toast.success(t('VEHICLES.TABLE_BULK_EDIT_SUCCESS'));
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(`API_ERRORS.${error.errorCode}`);
    },
  });

  const {
    costCentersOptions: costCenterOptions,
    isFetching: isCostCenterLoading,
    automaticAssignmentCostCenter,
  } = useGetCostCenters();

  // Get the current totalItems - prioritize actual data if available. Needed on first render.
  const totalItems =
    data?.pagination?.totalItems !== undefined ? data.pagination.totalItems : totalItemsRef.current;

  useDeepCompareEffect(() => {
    setPagination((current) => ({
      ...current,
      pageIndex: 0,
    }));
  }, [debouncedTableSearchQuery, tableFilters]);

  const table = useReactTable({
    data: data?.data ?? [],
    columns: columns,
    state: {
      globalFilter: tableSearchQuery,
      columnFilters,
      rowSelection,
      pagination,
      sorting,
    },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    rowCount: totalItems,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    enableRowSelection: true,
    getRowId: (row) => row.id as unknown as string,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setTableSearchQuery,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newSelection);

      const visibleRows = table.getRowModel().rows;
      let updatedSelectedData: VehicleTableDataDto[] = [...selectedData];

      visibleRows.forEach((row) => {
        const id = row.id;
        const isSelected = newSelection[id];
        const exists = updatedSelectedData.find(
          (r: VehicleTableDataDto) => r.id === row.original.id,
        );

        if (isSelected && !exists) {
          const newRow = row.original as VehicleTableDataDto;
          updatedSelectedData.push(newRow);
        } else if (!isSelected && exists) {
          updatedSelectedData = updatedSelectedData.filter(
            (r: VehicleTableDataDto) => r.id !== row.original.id,
          );
        }
      });

      setSelectedData(updatedSelectedData);
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  const selectedRowsIds = Object.keys(rowSelection);

  const initialSelectedCostCenters = useMemo(() => {
    return Array.from(
      new Set(selectedData.flatMap((row) => row.costCenters || []).map(({ id }) => id)),
    );
  }, [selectedData]);

  const bulkEditCostCenters = useCallback(
    (costCenters: string[]) => {
      bulkEditVehiclesMutation({
        ids: selectedRowsIds,
        costCenters,
      });
    },
    [bulkEditVehiclesMutation, selectedRowsIds],
  );

  // Update the ref whenever we get valid data
  useEffect(() => {
    if (data?.pagination?.totalItems !== undefined) {
      totalItemsRef.current = data.pagination.totalItems;
    }
  }, [data?.pagination?.totalItems]);

  useEffect(() => {
    setSelectedCostCenters(initialSelectedCostCenters);
  }, [initialSelectedCostCenters]);

  const handleCostCenterSelectionChanged = (costCenters: string[]) => {
    if (!costCenters?.length && automaticAssignmentCostCenter) {
      setSelectedCostCenters([automaticAssignmentCostCenter?.id]);
    }
  };

  return {
    globalFilter: table.getState().globalFilter,
    columnsLength: columns.length,
    isBulkEditVehiclesLoading,
    setSelectedCostCenters: handleCostCenterSelectionChanged,
    refetchTable: refetch,
    bulkEditCostCenters,
    isCostCenterLoading,
    selectedCostCenters,
    costCenterOptions,
    isFiltersLoading,
    selectedRowsIds,
    filtersOptions,
    filtersFrom,
    totalItems,
    isLoading,
    table,
    data,
  };
};
